import React from 'react';
import { Form, Field, ErrorMessage, useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { Input } from '@/_metronic/_partials/controls';
import { useParams } from 'react-router-dom';
import { createAiPromptQuestion, updateAiPromptQuestion } from './services/aiSettingAction';
import { useAiPromptQuestion } from './hooks/aiPromptHooks';
import Swal from 'sweetalert2';
import { useIntl } from 'react-intl';
import { navMenu } from '../contants';
import { useHistory } from 'react-router-dom';
import { Box } from '@mui/material';

// --- 1. Initial Values ---
const initialValues = {
  content: '',
  placeholder: '',
  relatedLinks: '',
  notes: '',
  status: 'active'
};

// --- 2. Validation Schema ---
const validationSchema = Yup.object({
  content: Yup.string()
    .required('The Question field is required.')
    .min(3, 'Question must be at least 3 characters.'),

  placeholder: Yup.string(),

  // This field is optional based on the original form
  relatedLinks: Yup.string(),

  notes: Yup.string(),

  status: Yup.string()
    .oneOf(['active', 'inactive'], 'Invalid status selected.')
    .required('A status must be selected.')
});

const AddNewQuestionForm = () => {
  const params = useParams();
  const questionId = params.questionId;
  const { data: questionData } = useAiPromptQuestion(questionId);
  const history = useHistory();
  const intl = useIntl();
  const newType = new URLSearchParams(window.location.search).get('type');

  const handleSubmit = async values => {
    try {
      const formattedLinks = values.relatedLinks
        ? values.relatedLinks
            .split(',')
            .map(link => link.trim())
            .filter(link => link !== '')
        : [];
      const commonBody = {
        content: values.content,
        placeholder: values.placeholder,
        notes: values.notes,
        status: values.status,
        links: formattedLinks
      };
      if (questionId) {
        await updateAiPromptQuestion(questionId, commonBody);
      } else {
        await createAiPromptQuestion({ ...commonBody, type: newType });
      }
      Swal.fire({
        text: intl.formatMessage({ id: 'success' }),
        icon: 'success',
        timer: 1500
      });

      const typeTab = questionData ? questionData.type : newType;

      history.push({
        pathname: '/shop/management',
        search: `?menu=${navMenu.AI_SETTING}&type=${typeTab}`,
        state: true
      });
    } catch (error) {
      console.error('Error creating AI prompt question:', error);
      Swal.fire({
        icon: 'error',
        text: error.response?.data?.message || 'An error occurred while saving the question.'
      });
    }
  };

  const formik = useFormik({
    initialValues: questionData || initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit
  });

  const navigateToQuestionListPage = () => {
    history.push({
      pathname: '/shop/management',
      search: `?menu=${navMenu.AI_SETTING}&type=${questionData?.type || newType}`,
      state: true
    });
  };

  return (
    <FormikProvider value={formik}>
      <Form className="p-4 border rounded">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <h3 className="mb-0">{questionId ? 'Edit Question' : 'Add New Question'}</h3>
          <button type="button" className="btn btn-secondary" onClick={navigateToQuestionListPage}>
            Back
          </button>
        </Box>

        <hr />

        <h4>Question Details</h4>

        <div className="form-group mb-3">
          <Field
            name="content"
            component={Input}
            label="Question"
            type="text"
            placeholder="Make it clear and concise for users to understand"
          />
        </div>

        <div className="form-group mb-3">
          <Field name="placeholder" component={Input} label="Answer Placeholder" type="text" />
        </div>

        <div className="form-group mb-3">
          <Field
            name="relatedLinks"
            component={Input}
            label="Related Links (Optional)"
            rows={3}
            placeholder="Add URLs separated by commas (e.g., https://help.example.com/guide)"
          />
        </div>

        <div className="form-group mb-3">
          <Field
            name="notes"
            component={Input}
            label="Additional Notes (Optional)"
            rows={3}
            placeholder="Add any additional context or notes"
          />
        </div>

        <hr />

        <h4>Settings</h4>

        <div className="form-group mb-4">
          <label className="d-block">Status</label>
          <div className="form-check form-check-inline">
            <Field type="radio" name="status" value="active" id="status-active" className="form-check-input" />
            <label className="form-check-label" htmlFor="status-active">
              Active
            </label>
          </div>

          <div className="form-check form-check-inline">
            <Field type="radio" name="status" value="inactive" id="status-inactive" className="form-check-input" />
            <label className="form-check-label" htmlFor="status-inactive">
              Inactive
            </label>
          </div>
          <ErrorMessage name="status" component="div" className="text-danger mt-1" />
        </div>

        <button type="submit" className="btn btn-primary" disabled={formik.isSubmitting}>
          Save Question
        </button>
      </Form>
    </FormikProvider>
  );
};

export default AddNewQuestionForm;
